import dayjs from 'dayjs';

/** @typedef {'young'|'breeding_ready'|'adult_female'|'adult_male'|'aged'} LifecycleTier */

export const LIFECYCLE_TIERS = [
  'young',
  'breeding_ready',
  'adult_female',
  'adult_male',
  'aged',
];

export function normalizeSpeciesSlug(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    return normalizeSpeciesSlug(value.slug || value.name);
  }
  return String(value).trim().toLowerCase();
}

export function getLifecycleTierName(profiles, speciesSlug, tier) {
  const slug = normalizeSpeciesSlug(speciesSlug);
  const tierConfig = profiles?.[slug]?.[tier];
  return tierConfig?.name ?? null;
}

export function getSpeciesProfile(profiles, speciesSlug) {
  const slug = normalizeSpeciesSlug(speciesSlug);
  return profiles?.[slug] ?? null;
}

export function listLifecycleTierNamesForSpecies(profiles, speciesSlug) {
  const profile = getSpeciesProfile(profiles, speciesSlug);
  if (!profile) return [];

  return LIFECYCLE_TIERS.filter((tier) => profile[tier]).map((tier) => ({
    tier,
    name: profile[tier].name,
    ...profile[tier],
  }));
}

export function tierDisplayName(profiles, speciesSlug, tier, fallback) {
  return getLifecycleTierName(profiles, speciesSlug, tier) || fallback;
}

export function calculateAgeMonths({ dateOfBirth, fallbackAgeMonths }) {
  if (dateOfBirth && dayjs(dateOfBirth).isValid()) {
    const birth = dayjs(dateOfBirth);
    const now = dayjs();
    return Math.max(0, now.diff(birth, 'month'));
  }
  if (typeof fallbackAgeMonths === 'number' && Number.isFinite(fallbackAgeMonths)) {
    return Math.max(0, Math.round(fallbackAgeMonths));
  }
  if (
    typeof fallbackAgeMonths === 'string'
    && fallbackAgeMonths.trim() !== ''
    && !Number.isNaN(Number(fallbackAgeMonths))
  ) {
    return Math.max(0, Math.round(Number(fallbackAgeMonths)));
  }
  return null;
}

const OPEN_BREEDING = new Set(['open', 'open_for_breeding']);

export function resolveLifecycleTierLabels({
  profiles,
  speciesSlug,
  gender,
  ageMonths,
  breedingStatus,
  isPregnant,
}) {
  const profile = getSpeciesProfile(profiles, speciesSlug);
  if (!profile || ageMonths == null) return [];

  const labels = [];
  const sex = (gender || '').toString().toLowerCase();
  const breeding = (breedingStatus || '').toString().toLowerCase();

  if (profile.young && ageMonths <= profile.young.max_months) {
    labels.push({ tier: 'young', name: profile.young.name });
  }

  if (
    profile.breeding_ready
    && sex === 'female'
    && ageMonths >= profile.breeding_ready.min_months
    && ageMonths <= profile.breeding_ready.max_months
  ) {
    const requireOpen = profile.breeding_ready.require_open_breeding !== false;
    const openOk = !requireOpen || OPEN_BREEDING.has(breeding);
    const notPregnant = !isPregnant;
    if (openOk && notPregnant) {
      labels.push({ tier: 'breeding_ready', name: profile.breeding_ready.name });
    }
  }

  const youngMax = profile.young?.max_months ?? 0;

  if (
    profile.adult_female
    && sex === 'female'
    && ageMonths >= profile.adult_female.min_months
    && ageMonths > youngMax
  ) {
    labels.push({ tier: 'adult_female', name: profile.adult_female.name });
  }

  if (
    profile.adult_male
    && sex === 'male'
    && ageMonths >= profile.adult_male.min_months
    && ageMonths > youngMax
  ) {
    labels.push({ tier: 'adult_male', name: profile.adult_male.name });
  }

  if (profile.aged) {
    const minMonths = profile.aged.min_years * 12;
    if (ageMonths >= minMonths) {
      labels.push({ tier: 'aged', name: profile.aged.name });
    }
  }

  return labels;
}

export function formatSpeciesLifecycleSummary(category, profiles) {
  const slug = normalizeSpeciesSlug(category);
  const tiers = listLifecycleTierNamesForSpecies(profiles, slug);
  if (!tiers.length) return null;
  return tiers.map((t) => t.name).join(' · ');
}
